import { createRouter, createWebHistory } from "vue-router";
import Store from './store/index.js';
import { logger } from './utils/logger';
import RedirectUrl from './utils/redirectUrl';

const EmptyRouterView = () => import('./views/pages/EmptyRouterView.vue');
const Home = () => import('./views/pages/Home.vue');
const Documents = () => import('./views/pages/Documents.vue');
const Profile = () => import('./views/pages/Profile.vue');
const InvalidLink = () => import('./views/pages/InvalidLink.vue');
const CompletedTaskLink = () => import('./views/pages/CompletedTaskLink.vue');
const OAuthIntegration = () => import('./views/pages/OAuthIntegration.vue');

let router = createRouter({
  history: createWebHistory('/'),
  base: '/sign-only/',

  routes: [

    {
      path: "",
      name: "root",
      meta: {
        authorize: true,
      }
    },

    {
      path: '/oauth-integration',
      name: 'oauthIntegration',
      component: OAuthIntegration,
      meta: {
        authorize: true,
        needsOrganization: false
      }
    },

    {
      path: '/:organizationId',
      component: EmptyRouterView,
      name:'organizationRoot',
      meta: {
        authorize: true,
        usersOnly: true // No guests allowed.
      },
      children:[
        {
          path:'',
          name: 'orgChildRoot',
          meta: {
            authorize: true,
          },
        },
        {
          path: 'home',
          name: 'home',
          props: route => Object.assign({}, route.query),
          component: Home,
          meta: {
            authorize: true
          }
        },
        {
          path: 'documents',
          name: 'documents',
          component: Documents,
          meta: {
            authorize: true
          }
        },
        {
          path: 'document/:projectId/',
          name: 'document',
          props:true,
          component: () => import('./views/pages/Document.vue'),
          meta: {
            authorize: true
          },

          children: [
            {
              /* Optional "draftStage" parameter points to the different stages of drafting document
              * 1: Document selection
              * 2: Signers selection
              * 3: Sign configuration
              * 4: Recipients selection
              */
              path: 'draft/:draftStage([1-4])?',
              name: 'draft',
              props: true,
              component: () => import('./views/components/DocumentDraft.vue'),
              meta: {
                authorize: true
              }
            },
            {
              // Optional parameter "invitationToken" to allow guest access.
              path: 'signing-status/:invitationToken?',
              name: 'documentWaitingCompleted',
              props: true,
              component: () => import('./views/pages/DocumentWaitingCompleted.vue'),
              meta: {
                authorize: true,
                usersOnly: false
              }
            },
            {
              path: 'log',
              name: 'documentLog',
              props: true,
              component: () => import('./views/pages/DocumentWaitingCompleted.vue'),
              meta: {
                authorize: true,
                usersOnly: false
              }
            },
            {
              /* Optional parameter "editDetails" enables user signer details editing. */
              path: 'sign/:editDetails?',
              name: 'sign',
              props: true,
              component: () => import('./views/pages/Sign.vue'),
              meta: {
                authorize: true
              },
            },
            {
              path: 'view-comments',
              name: 'viewComments',
              props: true,
              component: () => import('./views/pages/Sign.vue'),
              meta: {
                authorize: true,
                usersOnly: false
              },
            },

            {
              path: 'sign-configuration',
              name: 'signConfiguration',
              props: true,
              component: () => import('./views/pages/SignConfiguration.vue'),
              meta: {
                authorize: true
              },
            },

          ],
        },
        {
          /* Optional parameter "editDetails" enables user signer details editing. */
          path: 'sign/:projectId/guest/:invitationToken/:taskId/:editDetails?',
          name: 'signGuest',
          component: () => import('./views/pages/Document.vue'),
          props: true,
          meta: {
            authorize: false, // No account required
            usersOnly: false // For guests also
          }
        },
        {
          path: 'profile',
          name: 'profile',
          component: Profile,
          meta: {
            authorize: true,
            usersOnly: true // No guests
          }
        },
      ]
    },
    {
      path: '/msg/:notificationToken',
      name: 'notification',
    },
    {
      path: '/404',
      name: '404',
      component: InvalidLink,
    },
    {
      path: '/:pathMatch(.*)*',
      name:'404',
      component: InvalidLink,
    }, // if no match, go to 404
    {
      path: '/completedTaskGuest',
      name:'completedTaskGuest',
      component: CompletedTaskLink,
    },
  ],
});

router.beforeEach(async (to, from) => {

  // If the user is accessed via a notification short link, redirect to the full URL (DOS-3075)
  if(to.name == 'notification') {
    try {
      await RedirectUrl.redirectToNotificationUrl(to.params.notificationToken);
    }
    catch(error) {
    }
  }

  let organizationId = to.params.organizationId;

  // 1. Check for pending login token.
  let hasPendingLogin = await Store.dispatch("checkForLoginToken");

  if(hasPendingLogin === true) {

    // If we have a page we wanted to navigate to before login, load it
    let pageBeforeLogin = sessionStorage.getItem("soflowPageBeforeLogin");

    if(pageBeforeLogin) {
      sessionStorage.removeItem("soflowPageBeforeLogin");
      return ({
        path: pageBeforeLogin,
      });
    }
    else if(to.name === 'sign') {
      return ({
        name: 'sign',
        params: {
          organizationId: Store.getters.organizationId,
          projectId: to.params.projectId
        },
      });
    }
    else if(to.name === 'documentWaitingCompleted') {
      return ({
        name: 'documentWaitingCompleted',
        params: {
          organizationId: Store.getters.organizationId,
          projectId: to.params.projectId
        },
      });
    }
    else {
      return ({
        name: 'home',
        params: {
          organizationId: Store.getters.organizationId,
        },
      });
    }
  }
  // Note: The "signing-status" page may be accessed with an invitation token (SOFLOW-284).
  if(to.name === 'signGuest' || (to.name === 'documentWaitingCompleted' && to.params.invitationToken)) {
    let params = {
      invitationToken: to.params.invitationToken,
      taskId: to.params.taskId
    }
    try {
      await Store.dispatch("loadGuestUserInformation", params);
    }
    catch(error) {
      if(error.response && error.response.data.errorType === 'TASK_COMPLETED') {
        return ({
          name: 'completedTaskGuest'
        });
      }
      else {
        return ({
          name: '404'
        });
      }
    }
  }

  // 2. Resolve user information, if available
  //
  // If user is logged in, isAuthenticated will change to true after this.

  if(!Store.getters.isAuthenticated) {
    await Store.dispatch("loadUserInformation");
  }

  // 3. If target page requires authorization, check for permissions.

  if(to.meta.authorize && !Store.getters.isAuthenticated) {

    let pageBeforeLogin = to.fullPath;
    logger.debug("Redirecting to log-in page, then coming back to",
                 pageBeforeLogin);

    sessionStorage.setItem("soflowPageBeforeLogin",pageBeforeLogin);
    Store.dispatch("redirectToLogin", {organizationId: organizationId});
  }

  let isGuest = Store.getters.getGuestCollaboratorId != null;

  // Redirect guests to '404'-pages when acessing views meant only for registered users, SO-124.
  if(to.meta.usersOnly && isGuest){
    return ({
      name: '404',
      // preserve current path
      params: { pathMatch: to.path.substring(1).split('/') },
      query: to.query,
      hash: to.hash,
    });
  }

  // User is navigating to a page with an address that does not include an organisation id and
  // exclusively does not require it to be defined.

  if(!organizationId && to.meta.needsOrganization === false)
    return;

  let loggedInOrgId = Store.getters.getUserOrganizationId;

  // Redirect user to the organization home page from root path when logged-in.
  if(to.name == 'root') {
    return ({
      name: 'home',
      params: {
        organizationId: loggedInOrgId,
      },
    });
  }

  // Redirect registered users from "organizationRoot" or "orgChildRoot" to home view.
  if(loggedInOrgId == organizationId && !isGuest &&
    (to.name == "organizationRoot" || to.name == "orgChildRoot")){
      return ({
        name: 'home',
        params: {
          organizationId: loggedInOrgId,
        },
      });
    }

  // User is navigating to a different organization page than where they are logged in to.

  if(organizationId && loggedInOrgId != organizationId && to.query.reload != 'true'){

    logger.error("Tried to navigate to another organization");

    return ({
      name: '404',
      // preserve current path
      params: { pathMatch: to.path.substring(1).split('/') },
      query: to.query,
      hash: to.hash,
    });
  }
})

export default router;
